// Model data for Tesla model SEO landing pages.
// Tesla facts here (year cutoffs, sound count, firmware) are aligned to the
// canonical source of truth in lib/tesla-facts.ts — match those values, do not
// drift. The sound count is interpolated from PUBLIC_SOUND_COUNT_LABEL (the
// single source of truth — never hardcode a count, which becomes a banned stale
// string); year cutoffs follow TESLA_FACTS.support.models; firmware uses the
// non-brittle 2023 Holiday Update phrasing, never a brittle point release.
import { PUBLIC_SOUND_COUNT_LABEL } from './public-sound-count'
import { TESLA_FACTS } from './tesla-facts'

export interface ModelInfo {
  slug: string
  name: string
  title: string
  /** H1 for the model landing page. Defaults to "Custom lock sounds for Tesla {name}" if unset. */
  h1?: string
  description: string
  metaDescription: string
  yearStart: number
  yearDisplay: string
  features: string[]
  faqs: Array<{ question: string; answer: string }>
}

export const modelData: Record<string, ModelInfo> = {
  'model-3': {
    slug: 'model-3',
    name: 'Model 3',
    title: `Tesla Model 3 Lock Sounds: Free Boombox/LockChime.wav Downloads (${PUBLIC_SOUND_COUNT_LABEL})`,
    h1: 'Tesla Model 3 lock sounds',
    description: 'Model 3 cars built September 2019 or later play a custom lock sound through the external pedestrian-warning speaker. Vine Boom, R2-D2, Star Wars and the rest are each named LockChime.wav and ready for the Boombox folder on a FAT32 USB drive.',
    metaDescription: `Free Tesla Model 3 lock sounds: ${PUBLIC_SOUND_COUNT_LABEL} WAV files, each named LockChime.wav and ready for Boombox/. Works on Model 3 built September 2019+. Copy to USB, done in ${TESLA_FACTS.install.label}.`,
    yearStart: 2019,
    yearDisplay: '2019–2026',
    features: ['Built Sept 2019 or later', 'Standard & Long Range', 'Performance variant', 'RWD & AWD'],
    faqs: [
      {
        question: 'Does Tesla Model 3 support custom lock sounds?',
        answer: 'Yes. Model 3 units built September 2019 or later have the external speaker required. Custom lock sounds arrived in Tesla\'s 2023 Holiday Update (software 2023.44). If you don\'t see the option, update from Controls > Software, then go to Toybox → Boombox → Lock Sound.',
      },
      {
        question: 'How do I install a custom lock sound on Model 3?',
        answer: `Download a sound. It's already named LockChime.wav. Create a "Boombox" folder on a FAT32 USB drive, place LockChime.wav inside it, insert the USB into ${TESLA_FACTS.usb.portNote}, and open Toybox → Boombox → Lock Sound, then choose USB.`,
      },
      {
        question: 'Which USB port should I use on the Model 3?',
        answer: `Use ${TESLA_FACTS.usb.portNote}. Format FAT32 or exFAT, place LockChime.wav inside a folder named "Boombox", path: Boombox/LockChime.wav.`,
      },
      {
        question: 'What format does Model 3 need for custom lock sounds?',
        answer: 'Tesla needs a WAV file named LockChime.wav. Every download here already uses that name. Copy it to a FAT32 USB drive inside a folder called "Boombox" and plug it in.',
      },
      {
        question: 'Can I change the lock chime on my Tesla Model 3?',
        answer: 'Yes. On your Model 3 touchscreen go to Toybox → Boombox → Lock Sound, then choose USB. You need a FAT32 USB drive with a file named LockChime.wav inside a folder called "Boombox" (full path: Boombox/LockChime.wav). Every sound on this page downloads already set up that way, no renaming needed. Requires a Model 3 built September 2019 or later.',
      },
    ],
  },
  'model-y': {
    slug: 'model-y',
    name: 'Model Y',
    title: 'Custom Tesla Model Y Lock Sounds: Free WAV + USB Instructions (2026)',
    description: `Cartoon effects, movie quotes and meme audio for the Model Y, each already named LockChime.wav. Copy one to USB, select it in Toybox, done in ${TESLA_FACTS.install.label}.`,
    metaDescription: `Custom Tesla Model Y lock sounds with step-by-step USB instructions. ${PUBLIC_SOUND_COUNT_LABEL} free WAV downloads (2020–2026). Download LockChime.wav, copy to USB, done in ${TESLA_FACTS.install.label}.`,
    yearStart: 2020,
    yearDisplay: '2020–2026',
    features: ['All model years', 'Standard & Long Range', 'Performance variant', 'RWD & AWD', `USB install in ${TESLA_FACTS.install.label}`],
    faqs: [
      {
        question: 'Does Tesla Model Y support custom lock sounds in 2026?',
        answer: 'Yes. Every Model Y has the external speaker required. Custom lock sounds arrived in Tesla\'s 2023 Holiday Update (software 2023.44). If you don\'t see the option, update from Controls > Software, then go to Toybox → Boombox → Lock Sound.',
      },
      {
        question: 'How do I install a custom lock sound on Model Y?',
        answer: `Download any sound. It's already named LockChime.wav. Create a "Boombox" folder on a FAT32 USB drive, place LockChime.wav inside it, plug into ${TESLA_FACTS.usb.portNote}, and open Toybox → Boombox → Lock Sound, then choose USB.`,
      },
      {
        question: 'Tesla Model Y 2026 USB instructions for lock sounds?',
        answer: `1) Download LockChime.wav from teslalocksound.com. 2) Format a USB drive FAT32. 3) Create a "Boombox" folder. 4) Copy LockChime.wav inside it. 5) Insert USB into ${TESLA_FACTS.usb.portNote}. 6) Go to Toybox → Boombox → Lock Sound, then choose USB.`,
      },
      {
        question: 'Is the Model Y lock sound installation reversible?',
        answer: 'Yes. Remove the USB drive or delete Boombox/LockChime.wav and the car falls back to the built-in chime. You can also switch the selection in Toybox → Boombox → Lock Sound from USB back to one of the built-in sounds.',
      },
      {
        question: 'Can I use any WAV file as my Model Y lock sound?',
        answer: 'Tesla needs a WAV file named LockChime.wav. Every download here is already 44.1 kHz / 16-bit and named correctly, place it inside a "Boombox" folder on your USB drive (path: Boombox/LockChime.wav).',
      },
    ],
  },
  'model-s': {
    slug: 'model-s',
    name: 'Model S',
    title: `Tesla Model S Custom Lock Sounds: Change Your Chime, ${PUBLIC_SOUND_COUNT_LABEL} Free WAV Files (2026)`,
    h1: 'Tesla Model S Custom Lock Sounds',
    description: 'Change the lock sound on any 2021-refresh-or-later Model S, including Plaid: synth riffs, film-score stings, R2-D2. Each is already named LockChime.wav. Copy one to USB and it plays every time you lock.',
    metaDescription: `Change your Tesla Model S lock sound (2021 refresh & later, incl. Plaid). ${PUBLIC_SOUND_COUNT_LABEL} free custom WAV files: Star Wars, R2-D2, Halo & more. Copy to USB, done in ${TESLA_FACTS.install.label}.`,
    yearStart: 2021,
    yearDisplay: '2021–2026',
    features: ['2021 refresh or later', 'Plaid & Long Range', 'External speaker required'],
    faqs: [
      {
        question: 'Does Tesla Model S support custom lock sounds?',
        answer: 'Yes, on 2021 refresh and later. Those cars have the external speaker required. Custom lock sounds arrived in Tesla\'s 2023 Holiday Update (software 2023.44). If you don\'t see the option, update from Controls > Software.',
      },
      {
        question: 'How do I install a custom lock sound on Model S?',
        answer: 'Download a sound. It\'s already named LockChime.wav. Create a "Boombox" folder on a FAT32 USB drive, place LockChime.wav inside it, plug into your Model S USB port, and select it under Toybox → Boombox → Lock Sound, then choose USB.',
      },
      {
        question: 'How do I change the custom lock sound on my Model S?',
        answer: 'Replace Boombox/LockChime.wav on your USB drive with a new file using the same name and path, then reinsert the drive. If the old sound keeps playing, re-open Toybox → Boombox → Lock Sound and re-select USB so the car re-reads the file.',
      },
      {
        question: 'Does the Model S Plaid support custom lock sounds?',
        answer: 'Yes. Plaid works the same as any 2021-refresh Model S, keep software current under Controls > Software.',
      },
      {
        question: 'Does a pre-2021 Model S support custom lock sounds?',
        answer: 'No. Custom lock sounds play through the external pedestrian-warning speaker, which pre-2021 Model S cars don\'t have. A software update alone won\'t add the feature. The 2021 refresh and later are required.',
      },
    ],
  },
  'model-x': {
    slug: 'model-x',
    name: 'Model X',
    title: `Best Tesla Model X Lock Sounds: Free Download, ${PUBLIC_SOUND_COUNT_LABEL} WAV Files (2026)`,
    description: "Sci-fi blasts, movie cues and more for the Model X. Each is already named LockChime.wav. Copy one to USB and you're set.",
    metaDescription: `Download free lock sounds for Tesla Model X (2021 refresh & later). ${PUBLIC_SOUND_COUNT_LABEL} WAV files: Star Wars, sci-fi & more. USB install in ${TESLA_FACTS.install.label}. No app, no conversion needed.`,
    yearStart: 2021,
    yearDisplay: '2021–2026',
    features: ['2021 refresh or later', 'Plaid & Long Range', '6- & 7-seat layouts'],
    faqs: [
      {
        question: 'Does Tesla Model X support custom lock sounds?',
        answer: 'Yes, on 2021 refresh and later. Those cars have the external speaker required. Custom lock sounds arrived in Tesla\'s 2023 Holiday Update (software 2023.44). Update from Controls > Software if you don\'t see the option. The sound plays when you lock, after the falcon-wing doors close.',
      },
      {
        question: 'How do I install a custom lock sound on Model X?',
        answer: 'Download a sound. It\'s already named LockChime.wav. Create a "Boombox" folder on a FAT32 USB drive, place LockChime.wav inside it, plug into your Model X, and select it under Toybox → Boombox → Lock Sound, then choose USB.',
      },
      {
        question: 'Does the Model X Plaid support custom lock sounds?',
        answer: 'Yes. Plaid behaves the same as any 2021-refresh Model X, keep software current under Controls > Software.',
      },
      {
        question: 'Can I hear the lock sound outside the Model X?',
        answer: 'Yes. The lock sound plays through the external speaker system (same one used for Boombox mode), audible outside the vehicle when you lock.',
      },
    ],
  },
  'cybertruck': {
    slug: 'cybertruck',
    name: 'Cybertruck',
    title: `Best Cybertruck Lock Sounds: Free Download, ${PUBLIC_SOUND_COUNT_LABEL} WAV Files (2026)`,
    description: 'Sci-fi blasts, industrial clanks and sound effects for the Cybertruck. Each is already named LockChime.wav. Copy one to USB and it plays every time you lock.',
    metaDescription: `Download free lock sounds for Tesla Cybertruck (2024–2026). ${PUBLIC_SOUND_COUNT_LABEL} WAV files: sci-fi, industrial, Star Wars & more. USB install in ${TESLA_FACTS.install.label}. No app needed.`,
    yearStart: TESLA_FACTS.support.firstModelYear.cybertruck,
    yearDisplay: '2024–2026',
    features: ['All Cybertrucks', 'Foundation Series', 'AWD & Cyberbeast'],
    faqs: [
      {
        question: 'Does Tesla Cybertruck support custom lock sounds?',
        answer: 'Yes. Every Cybertruck has the external speaker required. Custom lock sounds shipped in Tesla\'s 2023 Holiday Update (software 2023.44); since the Cybertruck launched late 2023 most are already compatible. If you don\'t see the option, update from Controls > Software.',
      },
      {
        question: 'How do I install a custom lock sound on Cybertruck?',
        answer: 'Download a sound. It\'s already named LockChime.wav. Create a "Boombox" folder on a FAT32 USB drive, place LockChime.wav inside it, plug into the Cybertruck\'s USB port, and select it under Toybox → Boombox → Lock Sound, then choose USB.',
      },
      {
        question: 'What type of sound suits the Cybertruck aesthetic?',
        answer: 'Sci-fi, industrial, and futuristic sounds match the exoskeleton aesthetic well. The Sci-Fi category is a good place to start.',
      },
      {
        question: 'Is the Cyberbeast variant compatible with custom lock sounds?',
        answer: 'Yes. Foundation Series, AWD, and Cyberbeast all support custom lock sounds. The feature is software-based and identical across trims.',
      },
    ],
  },
}

export function getModelBySlug(slug: string): ModelInfo | undefined {
  return modelData[slug.toLowerCase()]
}

export function getAllModelSlugs(): string[] {
  return Object.keys(modelData)
}

// ─── Model vocabulary bridge (FilterRail codes ↔ canonical full names) ────────
// The /sounds filter rail renders compact model codes ("S"/"3"/"X"/"Y"/"CT"),
// but the query layer (lib/api/sounds.ts `compatibleModels`, which does
// `.overlaps('compatible_models', [name])`) and the SearchFilters bar both key
// on the full display name ("Model S", "Model 3", ..., "Cybertruck" — no
// "Model" prefix on Cybertruck). Full name is canonical everywhere filter
// state is stored/URL-synced; these maps exist only at the rail's UI boundary.
export const TESLA_MODEL_CODES = ['S', '3', 'X', 'Y', 'CT'] as const
export type TeslaModelCode = (typeof TESLA_MODEL_CODES)[number]

export const modelCodeToName: Record<TeslaModelCode, string> = {
  S: modelData['model-s'].name,
  '3': modelData['model-3'].name,
  X: modelData['model-x'].name,
  Y: modelData['model-y'].name,
  CT: modelData['cybertruck'].name,
}

export function modelNameToCode(name: string): TeslaModelCode | undefined {
  return (Object.keys(modelCodeToName) as TeslaModelCode[]).find(
    (code) => modelCodeToName[code] === name
  )
}
